import { useState } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { AddMemberModal, useAddMemberModal } from '../AddMemberModal'
import { RemoveGroupMemberModal, useRemoveGroupMemberModal } from '../RemoveGroupMemberModal'
import { ChangeRoleModal, useChangeRoleModal } from '../ChangeRoleModal'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQueryList } from '@percent/utility'
import {
  SearchInput,
  Flex,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableProvider,
  TableSearchAndFilters,
  TablePagination,
  Badge,
  Button,
  ButtonText,
  Menu,
  Icon
} from '@percent/lemonade'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import type { GroupMember } from '@percent/admin-dashboard/api/actions/groups/groups.types'
import type { SectionItem, SectionProps } from '@percent/lemonade'
import { isDefined } from '@percent/admin-dashboard/common/utility'
import type { GroupMembersProps } from './GroupMembers.types'

const getRoleBadgeVariant = (role: GroupMember['role']): 'default' | 'informative' | 'positive' => {
  switch (role) {
    case 'owner':
      return 'positive'
    case 'maintainer':
      return 'informative'
    default:
      return 'default'
  }
}

const getMemberDisplayName = (member: GroupMember): string => {
  if (member.type === 'account') {
    return member.fullName || member.email
  }

  return member.name
}

export function GroupMembers({ groupId, groupName, permissions }: GroupMembersProps) {
  const { groupService } = useServices()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [queryParams, setQueryParams] = useQueryParams({ search: StringParam })
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '')

  const [{ data, isLoading, totalResults, errorMessage, status }, { nextPage, previousPage, refresh }] = useQueryList(
    [`/groups/${groupId}/members`],
    groupService.getGroupMembers,
    {
      groupId,
      ...(queryParams.search && { search: queryParams.search })
    }
  )

  const addMemberModal = useAddMemberModal({ onSuccess: refresh })
  const removeGroupMemberModal = useRemoveGroupMemberModal({ onSuccess: refresh })
  const {
    isOpen: isChangeRoleOpen,
    member: changeRoleMember,
    openModal: openChangeRoleModal,
    closeModal: closeChangeRoleModal
  } = useChangeRoleModal()

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setQueryParams({ search: (event.target as HTMLInputElement).value })
    }

    if (event.key === 'Escape') {
      setSearchValue('')
      setQueryParams({ search: undefined })
    }
  }

  const handleClearValue = () => {
    setSearchValue('')
    setQueryParams({ search: undefined })
  }

  const handleMemberClick = (member: GroupMember) => {
    if (member.type === 'group') {
      navigate(`/accounts/groups/${member.id}`)
    }
  }

  const menuItem = (
    key: string,
    label: string,
    onClick: () => void,
    dataTestId: string,
    visible = true
  ): SectionItem | undefined => (visible ? { key, label, onClick, dataTestId } : undefined)

  const getMenuItems = (member: GroupMember): SectionProps[] => [
    {
      items: [
        menuItem(
          'remove',
          t('groupDetail.members.menu.removeMember'),
          () => {
            removeGroupMemberModal.openModal({
              groupId,
              memberId: member.id,
              memberName: getMemberDisplayName(member),
              groupName
            })
          },
          `member-remove-${member.id}`,
          permissions.can_remove_member
        ),
        menuItem(
          'changeRole',
          t('groupDetail.members.menu.changeRole'),
          () => {
            openChangeRoleModal(member)
          },
          `member-change-role-${member.id}`,
          permissions.can_set_member_role
        )
      ].filter(isDefined)
    }
  ]

  const columns = [
    { id: t('groupDetail.members.table.name'), isSortable: false, props: { width: '45%' } },
    { id: t('groupDetail.members.table.type'), isSortable: false, props: { width: '20%' } },
    { id: t('groupDetail.members.table.role'), isSortable: false, props: { width: '25%' } },
    { id: '', isSortable: false, props: { width: '10%' } }
  ]

  if (errorMessage && status === 'error') {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('groupDetail.members.emptyTable.title')}
        emptyTableDescription={t('groupDetail.members.emptyTable.description')}
      >
        <TableSearchAndFilters>
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              data-testid="group-members-search-input"
              placeholder={t('groupDetail.members.searchPlaceholder')}
              value={searchValue}
              onChange={handleKeyChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearValue}
              width="100%"
            />
            {permissions.can_add_member && (
              <Button variant="primary" onClick={addMemberModal.openModal} data-testid="add-member-button">
                {t('button.addNewMember')}
              </Button>
            )}
          </Flex>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {data?.map(member => (
              <TableRow key={member.id}>
                <TableCell>
                  {member.type === 'group' ? (
                    <ButtonText onClick={() => handleMemberClick(member)}>{getMemberDisplayName(member)}</ButtonText>
                  ) : (
                    getMemberDisplayName(member)
                  )}
                </TableCell>
                <TableCell>{t(`groupDetail.members.type.${member.type}`)}</TableCell>
                <TableCell>
                  <Badge variant={getRoleBadgeVariant(member.role)}>
                    {t(`groupDetail.members.role.${member.role}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Menu
                    sections={getMenuItems(member)}
                    customTriggerComponent={
                      <span style={{ cursor: 'pointer' }} data-testid={`member-menu-${member.id}`}>
                        <Icon name="menu-vertical" size={5} />
                      </span>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
        <AddMemberModal
          isOpen={addMemberModal.isOpen}
          onClose={addMemberModal.closeModal}
          groupId={groupId}
          onSuccess={addMemberModal.onSuccess}
        />
      </TableProvider>
      {removeGroupMemberModal.modalData && (
        <RemoveGroupMemberModal
          isOpen={removeGroupMemberModal.isOpen}
          onClose={removeGroupMemberModal.closeModal}
          groupId={removeGroupMemberModal.modalData.groupId}
          memberId={removeGroupMemberModal.modalData.memberId}
          memberName={removeGroupMemberModal.modalData.memberName}
          groupName={removeGroupMemberModal.modalData.groupName}
          onSuccess={removeGroupMemberModal.onSuccess}
        />
      )}
      <ChangeRoleModal
        isOpen={isChangeRoleOpen}
        onClose={closeChangeRoleModal}
        groupId={groupId}
        member={changeRoleMember}
        onSuccess={refresh}
      />
    </>
  )
}
